import React from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  checkboxClasses,
  CheckboxProps,
  Divider,
  DownIcon,
  FormControlLabel,
  FormControlLabelProps,
  Typography
} from "@carrotfertility/carotene-core"
import { FormCheckboxGroup, FormCheckBoxGroupProps, useWatch } from "@carrotfertility/carotene-core-x"
import { useIntl } from "react-intl"
import { FlattenedOption } from "#/components/carrot-plans/most-helpful-options/categorizedOptions"

interface RoutingFlowCheckboxGroupExpandableProps extends Omit<FormCheckBoxGroupProps, "options"> {
  maxSelections?: number
  flattenedOptions: FlattenedOption[]
  isExpanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

export function RoutingFlowCheckboxGroupExpandable({
  name,
  flattenedOptions,
  maxSelections,
  isExpanded,
  onExpandedChange,
  ...otherProps
}: RoutingFlowCheckboxGroupExpandableProps): JSX.Element {
  const selectedOptions = useWatch({ name }) || []
  const intl = useIntl()

  const initialOptions = flattenedOptions.filter((opt) => opt.isInitial)
  const showMoreOptions = flattenedOptions.filter((opt) => !opt.isInitial)

  const renderCheckbox = ({
    optionData,
    checkboxProps,
    formControlLabelProps
  }: {
    optionData: FlattenedOption
    checkboxProps: Omit<CheckboxProps, "sx">
    formControlLabelProps: Pick<FormControlLabelProps, "sx" | "label">
  }) => {
    return (
      <FormControlLabel
        key={optionData.value}
        label={optionData.label}
        {...formControlLabelProps}
        sx={(theme) => ({
          padding: theme.spacing(theme.tokens.spacing.sm),
          borderRadius: theme.tokens.borderRadius.md,
          borderStyle: "solid",
          borderWidth: "2px",
          inlineSize: "fit-content",
          alignItems: "center",
          borderColor: checkboxProps.checked ? theme.palette.secondary.main : "transparent",
          [`:not(.${checkboxClasses.disabled}):hover`]: {
            bgcolor: theme.palette.secondary.light
          },
          [`&.${checkboxClasses.disabled}`]: {
            borderColor: checkboxProps.checked ? theme.palette.action.disabled : "transparent"
          },
          "& .MuiFormControlLabel-label": {
            marginBlockStart: 0
          }
        })}
        control={
          <Checkbox
            checked={checkboxProps.checked}
            onChange={checkboxProps.onChange}
            name={checkboxProps.name}
            value={optionData.value}
            disabled={maxSelections && selectedOptions?.length >= maxSelections && !checkboxProps.checked}
            color={checkboxProps.color}
          />
        }
      />
    )
  }

  return (
    <Box>
      <FormCheckboxGroup
        name={name}
        options={initialOptions}
        sx={{ inlineSize: "100%" }}
        {...otherProps}
        renderItem={({ formControlLabelProps, checkboxProps }) => {
          const optionData = initialOptions.find((opt) => opt.value === checkboxProps.value)
          if (!optionData || optionData.isCategory) return null
          return renderCheckbox({ optionData, checkboxProps, formControlLabelProps })
        }}
      />

      {showMoreOptions.length > 0 && (
        <Accordion expanded={isExpanded} onChange={() => onExpandedChange(!isExpanded)}>
          <AccordionSummary
            expandIcon={<DownIcon fontSize="medium" />}
            aria-controls="show-more-content"
            id="show-more-header"
          >
            <Typography variant="button">
              {isExpanded
                ? intl.formatMessage({ defaultMessage: "Hide more options" })
                : intl.formatMessage({ defaultMessage: "Show more options" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormCheckboxGroup
              name={name}
              options={showMoreOptions}
              sx={{ inlineSize: "100%" }}
              {...otherProps}
              renderItem={({ formControlLabelProps, checkboxProps }) => {
                const optionData = showMoreOptions.find((opt) => opt.value === checkboxProps.value)
                if (!optionData) return null

                if (optionData.isCategory) {
                  if (optionData.label === null) return null
                  return (
                    <Box key={optionData.value} width="100%">
                      <Divider
                        sx={(theme) => ({
                          marginBlockStart: theme.spacing(theme.tokens.spacing.sm),
                          marginBlockEnd: theme.spacing(theme.tokens.spacing.lg)
                        })}
                      />
                      <Typography
                        sx={(theme) => ({
                          marginBlockEnd: theme.spacing(theme.tokens.spacing.xs)
                        })}
                      >
                        {optionData.label}
                      </Typography>
                    </Box>
                  )
                }

                return renderCheckbox({ optionData, checkboxProps, formControlLabelProps })
              }}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
}
